import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSettings, FiToggleLeft, FiToggleRight, FiCheck, FiX, FiLoader, FiExternalLink, FiShield } = FiIcons;

const PaymentGatewayCard = ({ gateway, onUpdate, onTest }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleToggleEnabled = async () => {
    await onUpdate(gateway.id, { ...gateway, isEnabled: !gateway.isEnabled });
  };

  const handleToggleMode = async () => {
    await onUpdate(gateway.id, { ...gateway, isLive: !gateway.isLive });
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      const result = await onTest(gateway.id, gateway.config);
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setIsTesting(false);
    }
  };

  const getGatewayIcon = () => {
    switch (gateway.id) {
      case 'stripe': return '💳';
      case 'razorpay': return '🇮🇳';
      case 'paypal': return '🅿️';
      default: return '💰';
    }
  };

  const getStatusColor = () => {
    if (!gateway.isEnabled) return 'text-gray-500';
    return gateway.isLive ? 'text-green-600' : 'text-blue-600';
  };

  const getStatusText = () => {
    if (!gateway.isEnabled) return 'Disabled';
    return gateway.isLive ? 'Live Mode' : 'Test Mode';
  };

  return (
    <motion.div
      className="bg-white rounded-xl p-6 shadow-soft border border-gray-200 hover:shadow-medium transition-all duration-300"
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
            {getGatewayIcon()}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">{gateway.name}</h3>
            <p className={`text-sm font-medium ${getStatusColor()}`}>
              {getStatusText()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            title="Configure"
          >
            <SafeIcon icon={FiSettings} className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleToggleEnabled}
            className="p-1"
            title={gateway.isEnabled ? 'Disable Gateway' : 'Enable Gateway'}
          >
            <SafeIcon 
              icon={gateway.isEnabled ? FiToggleRight : FiToggleLeft} 
              className={`w-8 h-8 ${gateway.isEnabled ? 'text-green-500' : 'text-gray-400'}`} 
            />
          </button>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-600 text-sm mb-4">{gateway.description}</p>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600">Supported Methods</p>
          <p className="font-bold text-gray-800">{gateway.config.payment_methods?.length || 0}</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600">Currencies</p>
          <p className="font-bold text-gray-800">{gateway.config.supported_currencies?.length || 0}</p>
        </div>
      </div>

      {/* Test Connection */}
      {gateway.isEnabled && (
        <div className="mb-4">
          <button
            onClick={handleTestConnection}
            disabled={isTesting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            {isTesting ? (
              <>
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                <span>Testing...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiShield} className="w-4 h-4" />
                <span>Test Connection</span>
              </>
            )}
          </button>
          
          {testResult && (
            <div className={`mt-2 p-2 rounded text-sm ${
              testResult.success 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={testResult.success ? FiCheck : FiX} className="w-4 h-4" />
                <span>{testResult.message}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expanded Configuration */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-gray-200 pt-4 space-y-4"
        >
          {/* Mode Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800">Environment Mode</p>
              <p className="text-xs text-gray-600">Switch between test and live mode</p>
            </div>
            <button
              onClick={handleToggleMode}
              className="flex items-center space-x-2"
              disabled={!gateway.isEnabled}
            >
              <span className={`text-sm ${gateway.isLive ? 'text-green-600' : 'text-blue-600'}`}>
                {gateway.isLive ? 'Live' : 'Test'}
              </span>
              <SafeIcon 
                icon={gateway.isLive ? FiToggleRight : FiToggleLeft} 
                className={`w-6 h-6 ${
                  !gateway.isEnabled 
                    ? 'text-gray-300' 
                    : gateway.isLive 
                      ? 'text-green-500' 
                      : 'text-blue-500'
                }`} 
              />
            </button>
          </div>

          {/* Payment Methods */}
          <div>
            <p className="font-medium text-gray-800 mb-2">Supported Payment Methods</p>
            <div className="flex flex-wrap gap-2">
              {gateway.config.payment_methods?.map((method) => (
                <span
                  key={method}
                  className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                >
                  {method.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>

          {/* Fees */}
          <div>
            <p className="font-medium text-gray-800 mb-2">Transaction Fees</p>
            <div className="space-y-1 text-xs text-gray-600">
              {Object.entries(gateway.fees).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="capitalize">{key.replace('_', ' ')}:</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex space-x-2">
            <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded text-sm transition-colors duration-200 flex items-center justify-center space-x-1">
              <SafeIcon icon={FiSettings} className="w-3 h-3" />
              <span>Configure</span>
            </button>
            <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded text-sm transition-colors duration-200 flex items-center justify-center space-x-1">
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
              <span>Dashboard</span>
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PaymentGatewayCard;